import { AxiosError } from 'axios';

/** Extract the HTTP status from a thrown API error (axios or plain). */
export function httpStatus(err: unknown): number | undefined {
  if (err instanceof AxiosError) return err.response?.status;
  const e = err as { response?: { status?: number }; status?: number };
  return e?.response?.status ?? e?.status;
}

export const isForbidden = (err: unknown) => httpStatus(err) === 403;      // hide/disable control
export const isConflict = (err: unknown) => httpStatus(err) === 409;       // version mismatch → reload

/**
 * Error code from the DemoOps `{ error: { code, message } }` envelope used by
 * 403/404/409 responses (Controller.php helpers). Distinguishes, e.g., a 409
 * `STALE_VERSION` (reload) from a 409 `INVALID_TRANSITION` (illegal action).
 */
export function apiErrorCode(err: unknown): string | undefined {
  const code = (err as { response?: { data?: { error?: { code?: unknown } } } })?.response?.data?.error?.code;
  return typeof code === 'string' ? code : undefined;
}

/** Server-authored message from the `{ error: { code, message } }` envelope, if present. */
export function apiErrorMessage(err: unknown): string | undefined {
  const msg = (err as { response?: { data?: { error?: { message?: unknown } } } })?.response?.data?.error?.message;
  return typeof msg === 'string' ? msg : undefined;
}

/**
 * The server's `error.details` object from the same envelope, typed by the
 * caller, or undefined. Used where a refusal carries a payload worth showing
 * — e.g. a 410 CODE_RETIRED names the hire the label used to be on.
 */
export function apiErrorDetails<T extends object>(err: unknown): T | undefined {
  const details = (err as { response?: { data?: { error?: { details?: unknown } } } })?.response?.data?.error?.details;
  return details != null && typeof details === 'object' ? (details as T) : undefined;
}

/** True when a 422 response flagged a specific field (Laravel errors bag). */
export function hasValidationField(err: unknown, field: string): boolean {
  const errors = (err as { response?: { data?: { errors?: Record<string, unknown> } } })?.response?.data?.errors;
  return !!errors && Array.isArray(errors[field]);
}

/**
 * The 422 bag as one message per field — the first Laravel gave, which is
 * the one it shows too. Empty for anything that is not a bag (an envelope
 * refusal, a network error), so a caller can key its inline messages off
 * this and put the general line on whatever is left.
 */
export function validationErrors(err: unknown): Record<string, string> {
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  const out: Record<string, string> = {};
  if (!errors || typeof errors !== 'object') return out;
  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(messages) && typeof messages[0] === 'string') out[field] = messages[0];
  }
  return out;
}

/** First validation message from a 422 response (specific field or any), if present. */
export function validationMessage(err: unknown, field?: string): string | undefined {
  const data = (err as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } })?.response?.data;
  const errors = data?.errors;
  if (errors && typeof errors === 'object') {
    if (field && Array.isArray(errors[field])) return errors[field][0];
    const first = Object.values(errors)[0];
    if (Array.isArray(first)) return first[0];
  }
  if (typeof data?.message === 'string') return data.message;

  // DemoOps's own envelope is { error: { code, message } } — the shape every
  // domain refusal uses. Without this the caller falls back to a generic
  // "check the highlighted fields", which is wrong twice over: the failure is
  // not a field error, and nothing is highlighted. Cost Michael eight
  // identical retries against INVOICE_ALREADY_CONVERTED.
  const envelope = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error;

  return typeof envelope?.message === 'string' ? envelope.message : undefined;
}

/** Human message for the common write failures the backend surfaces. */
export function writeErrorMessage(err: unknown): string {
  const s = httpStatus(err);
  if (s === 409) return 'This was changed by someone else. Please reload and try again.';
  if (s === 403) return 'You do not have permission to do that.';
  if (s === 422) return 'Please check the highlighted fields.';
  return 'Something went wrong. Please try again.';
}
